import React, { useState, useEffect, useCallback } from "react";

/**
 * リハーサル日程の登録・編集フォーム（右から出るパネル）。
 *
 * - 種別タブで入力欄が変わる: 地方選抜=選抜エリア / 特別ナンバー=作品の選択。
 * - 会場（スタジオ名）は空のままにできる。日付だけ先に押さえて会場は後、という
 *   運用が実際にあるため（その場合は「未予約」で表示される）。
 * - 保存は admin の API 経由で recital2027_schedules に書く。
 */

const apiBase = process.env.REACT_APP_API_URL;

// 選抜エリアは地方選抜のある4つ。会場エリアは特別ナンバーのリハで使う滋賀も含める
// （API の SCHEDULE_REGIONAL_AREAS / SCHEDULE_VENUE_AREAS と同じ）。
const REGIONAL_AREAS = ["大阪", "京都", "兵庫", "奈良"];
const VENUE_AREAS = ["大阪", "京都", "兵庫", "奈良", "滋賀"];

const KIND_TABS = [
  { value: "regional", label: "地方選抜" },
  { value: "headz", label: "HEADZ" },
  { value: "special", label: "特別ナンバー" },
];

// 種別は special_work_id の有無で決まる（API と同じ規則）
const kindOf = (s) =>
  s.special_work_id != null || s.kind === "special" ? "special" : s.kind;

const emptyForm = (date, defaults) => ({
  kind: "regional",
  regional_area: "大阪",
  special_work_id: "",
  date: date || "",
  start: "12:00",
  end: "15:00",
  venue_area: "大阪",
  studio: "",
  is_booked: false,
  note: "",
  // 呼び出し側が追加時の初期値を差し込める（特別ナンバー管理から作品を選んだ状態で開く等）
  ...(defaults || {}),
});

const formFrom = (row) => ({
  kind: kindOf(row),
  regional_area: row.regional_area || "大阪",
  special_work_id: row.special_work_id != null ? String(row.special_work_id) : "",
  date: row.date || "",
  start: row.start || "",
  end: row.end || "",
  venue_area: row.venue_area || "大阪",
  studio: row.studio || "",
  is_booked: !!row.is_booked,
  note: row.note || "",
});

function ScheduleFormDrawer({ open, target, defaultDate, defaults, works, onClose, onSaved }) {
  const [form, setForm] = useState(() => emptyForm(defaultDate, defaults));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!target;

  // 開くたびに中身を作り直す（前回の入力が残らないように）
  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(target ? formFrom(target) : emptyForm(defaultDate, defaults));
  }, [open, target, defaultDate, defaults]);

  const set = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    setError("");
    if (!form.date) {
      setError("日付を入れてください。");
      return;
    }
    if (form.kind === "special" && !form.special_work_id) {
      setError("特別ナンバーの作品を選んでください。");
      return;
    }
    const path = isEdit
      ? "update_recital2027_schedule"
      : "create_recital2027_schedule";
    const body = {
      ...form,
      special_work_id:
        form.kind === "special" ? Number(form.special_work_id) : null,
      ...(isEdit ? { id: target.id } : {}),
    };
    try {
      setSaving(true);
      const res = await fetch(`${apiBase}/api/${path}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (e) {
      console.error("リハーサル日程の保存に失敗しました:", e);
      setError(e.message || "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, target, onSaved]);

  return (
    <aside className={`sch-drawer ${open ? "show" : ""}`}>
      <div className="dhead">
        <h2>{isEdit ? "日程を編集" : "日程を追加"}</h2>
        <button type="button" className="close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="dbody">
        <div className="form-grid">
          <div className="f-row">
            <label>種別</label>
            <div className="kind-tabs">
              {KIND_TABS.map((k) => (
                <button
                  type="button"
                  key={k.value}
                  className={form.kind === k.value ? "active" : ""}
                  onClick={() => set("kind", k.value)}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          {form.kind === "regional" && (
            <div className="f-row">
              <label>選抜エリア</label>
              <select
                value={form.regional_area}
                onChange={(e) => set("regional_area", e.target.value)}
              >
                {REGIONAL_AREAS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          )}

          {form.kind === "special" && (
            <div className="f-row">
              <label>作品</label>
              <select
                value={form.special_work_id}
                onChange={(e) => set("special_work_id", e.target.value)}
              >
                <option value="">選んでください</option>
                {works.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.title}
                  </option>
                ))}
              </select>
              <span className="hint">
                特別ナンバーの申請作品から選びます
              </span>
            </div>
          )}

          <div className="f-row">
            <label>日付</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
            />
          </div>

          <div className="f-two">
            <div className="f-row">
              <label>開始</label>
              <input
                type="time"
                value={form.start}
                onChange={(e) => set("start", e.target.value)}
              />
            </div>
            <div className="f-row">
              <label>終了</label>
              <input
                type="time"
                value={form.end}
                onChange={(e) => set("end", e.target.value)}
              />
            </div>
          </div>

          <div className="f-row">
            <label>会場エリア</label>
            <select
              value={form.venue_area}
              onChange={(e) => set("venue_area", e.target.value)}
            >
              {VENUE_AREAS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          <div className="f-row">
            <label>スタジオ名</label>
            <input
              type="text"
              value={form.studio}
              placeholder="例: スプラウト関テレ前店701（未定なら空のまま）"
              onChange={(e) => set("studio", e.target.value)}
            />
          </div>

          <div className="f-row">
            <label className="f-check">
              <input
                type="checkbox"
                checked={form.is_booked}
                onChange={(e) => set("is_booked", e.target.checked)}
              />
              会場を予約済みにする
            </label>
            <span className="hint">
              外すと「未予約」（破線・赤バッジ）で表示されます
            </span>
          </div>

          <div className="f-row">
            <label>メモ</label>
            <textarea
              value={form.note}
              placeholder="例: 11/1から変更"
              onChange={(e) => set("note", e.target.value)}
            />
          </div>

          {error && <div className="f-error">{error}</div>}

          <div className="f-actions">
            <button type="button" className="btn" onClick={onClose}>
              キャンセル
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "保存中…" : "保存する"}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default ScheduleFormDrawer;
